import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StorePerformanceInterval } from '../../../../../../constants';
import {
  QueueLengthCheckoutExperience,
  QueueLengthForecastPerformanceItem,
  QueueLengthPerformanceItem,
  TillType,
} from '../../../../../../hooks';
import { ColumnFlexContainer } from '../../../../../../stories/atoms/containerV2/ContainerV2';
import { muiTheme } from '../../../../../../theme';
import { homeTranslations, kpiTargetTranslations } from '../../../../../../translations';
import { WidgetMetricTile, WidgetMetricType } from '../../../../common/metricTile/WidgetMetricTile';
import { WidgetMetricTilesContainer } from '../../../../common/metricTile/WidgetMetricTile.styles';
import WidgetChart from '../../../../common/widgetChart/WidgetChart';
import { WidgetMobileLayout } from '../../../../common/widgetMobileLayout/WidgetMobileLayout';
import { calculatePercentage, getOverAllQueueInformation, isQueueLengthForecastPerformanceItem } from '../util';
import { formatAmountWithSeparator } from '../../../../../../utils';

export const GraphView = ({
  target,
  checkoutExperience,
  performanceItems,
  interval,
  filteredPerformanceItems,
  forecastPerformanceItems,
  isKpiLoading,
  isKpiError,
}: {
  target: number | null;
  checkoutExperience: QueueLengthCheckoutExperience | null;
  interval: StorePerformanceInterval;
  performanceItems: QueueLengthPerformanceItem[];
  filteredPerformanceItems: (QueueLengthPerformanceItem | null)[];
  forecastPerformanceItems: (QueueLengthPerformanceItem | null)[];
  isKpiLoading: boolean;
  isKpiError: boolean;
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  const { queueLength: queueLengthTranslations, common: commonTranslations } = homeTranslations;

  const getQueueLengthByTillType = (
    queueLengthPerformanceItems: QueueLengthPerformanceItem[],
    queueTillType: TillType,
  ) => {
    let totalQueueCount = 0;
    let totalTransactions = 0;

    queueLengthPerformanceItems.forEach((queueLengthPerformanceItem) => {
      const { tillData } = queueLengthPerformanceItem;

      tillData.forEach((queueLengthTillData) => {
        const { queueCount, transactions, tillType } = queueLengthTillData;

        if ((tillType === 'null' || tillType === 'DEFAULT') && queueTillType === 'DEFAULT') {
          totalQueueCount += queueCount;
          totalTransactions += transactions;
        }

        if ((tillType === 'SELFCHECKOUT' || tillType === 'SCO-MP') && queueTillType === 'SELFCHECKOUT') {
          totalQueueCount += queueCount;
          totalTransactions += transactions;
        }
      });
    });

    return (totalTransactions === 0 ? 0 : totalQueueCount / totalTransactions) * 100;
  };

  const regularTillQueueLength = getQueueLengthByTillType(performanceItems, 'DEFAULT');
  const scoTillQueueLength = getQueueLengthByTillType(performanceItems, 'SELFCHECKOUT');

  const itemValueResolver = (
    storePerformanceItem: QueueLengthPerformanceItem,
    seriesIndex?: number | undefined,
  ): number => {
    // Forecast data type does not match the dnormal data type. Need to manually cast here
    const item = storePerformanceItem as QueueLengthPerformanceItem | QueueLengthForecastPerformanceItem;

    if (!isQueueLengthForecastPerformanceItem(item)) {
      let totalQueueCount = 0;
      let totalTransactions = 0;

      storePerformanceItem.tillData.forEach((tillData) => {
        if (seriesIndex === 0 && (tillData.tillType === 'DEFAULT' || tillData.tillType === 'null')) {
          totalQueueCount += tillData.queueCount;
          totalTransactions += tillData.transactions;
        }

        if ((seriesIndex === 1 && tillData.tillType === 'SELFCHECKOUT') || tillData.tillType === 'SCO-MP') {
          totalQueueCount += tillData.queueCount;
          totalTransactions += tillData.transactions;
        }
      });

      return (totalTransactions === 0 ? 0 : totalQueueCount / totalTransactions) * 100;
    }

    const totalQueueCount = item.defaultQueueCount + item.scoQueueCount;
    const totalTransactions = item.defaultTransactionsCount + item.scoTransactionsCount;

    return (totalTransactions === 0 ? 0 : totalQueueCount / totalTransactions) * 100;
  };

  const generateChart = (isMobileChart: boolean) => {
    return (
      <WidgetChart
        type="bar"
        seriesCount={2}
        performanceItemsChartName={[
          t(queueLengthTranslations.tabDefault.key, queueLengthTranslations.tabDefault.defaultValue),
          t(queueLengthTranslations.tabSelfCheckout.key, queueLengthTranslations.tabSelfCheckout.defaultValue),
        ]}
        performanceItems={performanceItems}
        filteredPerformanceItems={filteredPerformanceItems}
        forecastPerformanceItems={forecastPerformanceItems}
        tooltipFormatter={(value) => formatAmountWithSeparator(value, '%')}
        interval={interval}
        showXAxisTooltip={isMobileChart}
        itemValueResolver={itemValueResolver}
        showXAxis={true}
        chartColors={['validationGreen', 'inActiveGray', 'tagBlue']}
        dashArray={[0, 0, 4]}
        yAxisFormatter={(value) => `${value.toFixed()}%`}
        sparklineEnabled={isMobileChart}
        customYAxis={{
          show: !isMobileChart,
        }}
      />
    );
  };

  const getForecastIndicator = (percent: number) => {
    if (percent <= 100) return 'HAPPY';
    if (percent <= 103) return 'CONFUSED';
    return 'SAD';
  };

  const { overallQueueLengthPercentage, overallExperiencePercentage, totalQueueCount, totalTransactions } =
    getOverAllQueueInformation({ checkoutExperience, filteredPerformanceItems });

  const forecastTotalQueueCount =
    (forecastPerformanceItems as unknown as (QueueLengthForecastPerformanceItem | null)[]).reduce((acc, item) => {
      if (item === null) return acc;

      return acc + item.defaultQueueCount + item.scoQueueCount;
    }, 0) + totalQueueCount;

  const forecastTotalTransactions =
    (forecastPerformanceItems as unknown as (QueueLengthForecastPerformanceItem | null)[]).reduce((acc, item) => {
      if (item === null) return acc;

      return acc + item.defaultTransactionsCount + item.scoTransactionsCount;
    }, 0) + totalTransactions;

  const forecastQueueLengthPercentage = calculatePercentage(forecastTotalQueueCount, forecastTotalTransactions);

  const targetProgressPercent = target && target != 0 ? (forecastQueueLengthPercentage / target) * 100 : 0;

  const showForecast = !!target && forecastPerformanceItems.some((item) => item !== null);

  return (
    <ColumnFlexContainer padding={isMobile ? [2, 6] : [2]} data-testid="chart" flex={1} minHeight={[0]}>
      {!isMobile && (
        <>
          <WidgetMetricTilesContainer>
            {showForecast && (
              <WidgetMetricTile
                metricProperties={{
                  type: WidgetMetricType.ForecastMetric,
                  properties: {
                    metricValue: formatAmountWithSeparator(forecastQueueLengthPercentage, '%'),
                    forecastIndicator: getForecastIndicator(targetProgressPercent),
                    metricTooltipText: t(
                      commonTranslations.forecastTileTooltip.key,
                      commonTranslations.forecastTileTooltip.defaultValue,
                    ),
                  },
                }}
              />
            )}

            <WidgetMetricTile
              metricProperties={{
                type: WidgetMetricType.RegularMetric,
                properties: {
                  metricTitle: t(
                    queueLengthTranslations.overallQueueLength.key,
                    queueLengthTranslations.overallQueueLength.defaultValue,
                  ),
                  metricValue: formatAmountWithSeparator(overallQueueLengthPercentage, '%'),
                  metricTooltipText: t(
                    queueLengthTranslations.metricTileTooltip.queueLengthTile.key,
                    queueLengthTranslations.metricTileTooltip.queueLengthTile.defaultValue,
                  ),
                },
              }}
            />

            {target !== null && (
              <WidgetMetricTile
                isLoading={isKpiLoading}
                isError={isKpiError}
                metricProperties={{
                  type: WidgetMetricType.RegularMetric,
                  properties: {
                    metricTitle: t(
                      kpiTargetTranslations.kpiProperties.target.title.key,
                      kpiTargetTranslations.kpiProperties.target.title.defaultValue,
                    ),
                    metricValue: formatAmountWithSeparator(target, '%'),
                    metricTooltipText: t(
                      queueLengthTranslations.metricTileTooltip.targetTile.key,
                      queueLengthTranslations.metricTileTooltip.targetTile.defaultValue,
                    ),
                  },
                }}
              />
            )}

            <WidgetMetricTile
              metricProperties={{
                type: WidgetMetricType.RegularMetric,
                properties: {
                  metricTitle: t(
                    queueLengthTranslations.overallExperience.key,
                    queueLengthTranslations.overallExperience.defaultValue,
                  ),
                  metricValue: formatAmountWithSeparator(overallExperiencePercentage, '%'),
                  metricTooltipText: t(
                    queueLengthTranslations.metricTileTooltip.overallExperienceTile.key,
                    queueLengthTranslations.metricTileTooltip.overallExperienceTile.defaultValue,
                  ),
                },
              }}
            />

            {/* Regular Till Queue Length */}
            <WidgetMetricTile
              metricProperties={{
                type: WidgetMetricType.LegendMetric,
                properties: {
                  metricTitle: t(
                    queueLengthTranslations.tabDefault.key,
                    queueLengthTranslations.tabDefault.defaultValue,
                  ),
                  metricValue: formatAmountWithSeparator(regularTillQueueLength, '%'),
                  metricTooltipText: t(
                    queueLengthTranslations.metricTileTooltip.regularTillsTile.key,
                    queueLengthTranslations.metricTileTooltip.regularTillsTile.defaultValue,
                  ),
                  legendColor: 'validationGreen',
                },
              }}
            />

            {/* SCO Till Queue Length */}
            <WidgetMetricTile
              metricProperties={{
                type: WidgetMetricType.LegendMetric,
                properties: {
                  metricTitle: t(
                    queueLengthTranslations.tabSelfCheckout.key,
                    queueLengthTranslations.tabSelfCheckout.defaultValue,
                  ),
                  metricValue: formatAmountWithSeparator(scoTillQueueLength, '%'),
                  legendColor: 'inActiveGray',
                  metricTooltipText: t(
                    queueLengthTranslations.metricTileTooltip.selfCheckoutTile.key,
                    queueLengthTranslations.metricTileTooltip.selfCheckoutTile.defaultValue,
                  ),
                },
              }}
            />
          </WidgetMetricTilesContainer>

          <div style={{ flex: 1 }}>{generateChart(false)}</div>
        </>
      )}

      {isMobile && (
        <>
          {showForecast && (
            <WidgetMetricTile
              metricProperties={{
                type: WidgetMetricType.ForecastMetric,
                properties: {
                  metricValue: formatAmountWithSeparator(forecastQueueLengthPercentage, '%'),
                  forecastIndicator: getForecastIndicator(targetProgressPercent),
                  metricTooltipText: t(
                    commonTranslations.forecastTileTooltip.key,
                    commonTranslations.forecastTileTooltip.defaultValue,
                  ),
                },
              }}
            />
          )}

          <WidgetMobileLayout
            metricItems={[
              {
                label: t(
                  queueLengthTranslations.overallQueueLength.key,
                  queueLengthTranslations.overallQueueLength.defaultValue,
                ),
                content: formatAmountWithSeparator(overallQueueLengthPercentage, '%'),
                tooltipText: t(
                  queueLengthTranslations.metricTileTooltip.queueLengthTile.key,
                  queueLengthTranslations.metricTileTooltip.queueLengthTile.defaultValue,
                ),
              },
              {
                label: t(
                  kpiTargetTranslations.kpiProperties.target.title.key,
                  kpiTargetTranslations.kpiProperties.target.title.defaultValue,
                ),
                content: `${target !== null ? formatAmountWithSeparator(target, '%') : '-'}`,
                isLoading: isKpiLoading,
                isError: isKpiError,
                tooltipText: t(
                  queueLengthTranslations.metricTileTooltip.targetTile.key,
                  queueLengthTranslations.metricTileTooltip.targetTile.defaultValue,
                ),
              },
              {
                label: t(
                  queueLengthTranslations.overallExperience.key,
                  queueLengthTranslations.overallExperience.defaultValue,
                ),
                content: formatAmountWithSeparator(overallExperiencePercentage, '%'),
                tooltipText: t(
                  queueLengthTranslations.metricTileTooltip.overallExperienceTile.key,
                  queueLengthTranslations.metricTileTooltip.overallExperienceTile.defaultValue,
                ),
              },

              {
                label: t(queueLengthTranslations.tabDefault.key, queueLengthTranslations.tabDefault.defaultValue),
                content: formatAmountWithSeparator(regularTillQueueLength, '%'),
                tooltipText: t(
                  queueLengthTranslations.metricTileTooltip.regularTillsTile.key,
                  queueLengthTranslations.metricTileTooltip.regularTillsTile.defaultValue,
                ),
              },
              {
                label: t(
                  queueLengthTranslations.tabSelfCheckout.key,
                  queueLengthTranslations.tabSelfCheckout.defaultValue,
                ),
                content: formatAmountWithSeparator(scoTillQueueLength, '%'),
                tooltipText: t(
                  queueLengthTranslations.metricTileTooltip.selfCheckoutTile.key,
                  queueLengthTranslations.metricTileTooltip.selfCheckoutTile.defaultValue,
                ),
              },
            ]}
            renderChart={() => {
              return generateChart(true);
            }}
          />
        </>
      )}
    </ColumnFlexContainer>
  );
};
